/**
 * Timezone Utilities
 * Universal timezone handling functions for consistent date formatting across the application
 */

// ========================
// TIMEZONE CONFIGURATION
// ========================

/**
 * Application Timezone Configuration
 * 
 * Change this value to switch between timezone behaviors:
 * - 'LOCAL' - Uses device's local timezone (recommended for most cases)
 * - 'UTC' - Uses UTC timezone 
 * - 'EST' - Uses Eastern Standard Time
 * - 'PST' - Uses Pacific Standard Time
 * - 'CST' - Uses Central Standard Time
 * - 'MST' - Uses Mountain Standard Time
 * 
 * You can also use full timezone identifiers like:
 * - 'America/New_York'
 * - 'America/Los_Angeles' 
 * - 'America/Chicago'
 * - 'America/Denver'
 * - 'Europe/London'
 * - etc.
 */

// Default timezone configuration
let TIMEZONE_CONFIG = 'LOCAL';

// Try to load timezone from localStorage on initialization
try {
  const storedTimezone = localStorage.getItem('arcattend_timezone_config');
  if (storedTimezone) {
    TIMEZONE_CONFIG = storedTimezone;
  }
} catch (error) {
  console.warn('Failed to load timezone from localStorage:', error);
}

export { TIMEZONE_CONFIG };

// Timezone mapping for common abbreviations
const TIMEZONE_MAP = {
  'LOCAL': undefined, // Use local timezone
  'UTC': 'UTC',
  'EST': 'America/New_York',
  'PST': 'America/Los_Angeles', 
  'CST': 'America/Chicago',
  'MST': 'America/Denver'
};

/**
 * Get the timezone to use based on configuration
 * @returns {string|undefined} Timezone identifier or undefined for local
 */
const getConfiguredTimezone = () => {
  // Always get the latest timezone config (in case it was updated dynamically)
  const currentConfig = getCurrentTimezoneConfig();
  return TIMEZONE_MAP[currentConfig] || currentConfig;
};

/**
 * Format a date object to YYYY-MM-DD using configured timezone
 * @param {Date} date - The date object to format
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const formatDateLocal = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  
  const currentConfig = getCurrentTimezoneConfig();
  const timezone = getConfiguredTimezone();
  
  if (currentConfig === 'LOCAL' || !timezone) {
    // Use original local timezone method
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else if (timezone === 'UTC') {
    // Use UTC formatting
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    // Use Intl.DateTimeFormat for specific timezones
    try {
      const formatter = new Intl.DateTimeFormat('en-CA', { 
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return formatter.format(date); // Returns YYYY-MM-DD
    } catch (error) {
      console.warn(`Invalid timezone ${timezone}, falling back to local`, error);
      // Fallback to local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
};

/**
 * Format a date object to MM/DD/YYYY using configured timezone
 * @param {Date} date - The date object to format
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
export const formatDateLocalUS = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  
  const currentConfig = getCurrentTimezoneConfig();
  const timezone = getConfiguredTimezone();
  
  if (currentConfig === 'LOCAL' || !timezone) {
    // Use original local timezone method
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  } else if (timezone === 'UTC') {
    // Use UTC formatting
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  } else {
    // Use Intl.DateTimeFormat for specific timezones
    try {
      const formatter = new Intl.DateTimeFormat('en-US', { 
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return formatter.format(date); // Returns MM/DD/YYYY
    } catch (error) {
      console.warn(`Invalid timezone ${timezone}, falling back to local`, error);
      // Fallback to local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}/${day}/${year}`;
    }
  }
};

/**
 * Format a date object to a readable format using configured timezone
 * @param {Date} date - The date object to format
 * @returns {string} Formatted date string like "August 31, 2025"
 */
export const formatDateReadable = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  
  const currentConfig = getCurrentTimezoneConfig();
  const timezone = getConfiguredTimezone();
  
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  if (timezone && currentConfig !== 'LOCAL') {
    options.timeZone = timezone;
  }
  
  return date.toLocaleDateString('en-US', options);
};

/**
 * Get the current date in the configured timezone
 * @returns {Date} Current date adjusted for configured timezone
 */
export const getCurrentDate = () => {
  const now = new Date();
  const currentConfig = getCurrentTimezoneConfig();
  const timezone = getConfiguredTimezone();
  
  if (currentConfig === 'LOCAL' || !timezone) {
    return now;
  } else if (timezone === 'UTC') {
    return now; // Date object is already in UTC internally
  } else {
    // For specific timezones, we need to create a date that represents
    // the current time in that timezone
    try {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const parts = formatter.formatToParts(now);
      const partsMap = {};
      parts.forEach(part => {
        partsMap[part.type] = part.value;
      });
      
      // Create a new date in the target timezone
      return new Date(
        parseInt(partsMap.year),
        parseInt(partsMap.month) - 1,
        parseInt(partsMap.day),
        parseInt(partsMap.hour || '0'),
        parseInt(partsMap.minute || '0'),
        parseInt(partsMap.second || '0')
      );
    } catch (error) {
      console.warn(`Error creating date in timezone ${timezone}, using local`, error);
      return now;
    }
  }
};

/**
 * Get today's date in YYYY-MM-DD format using configured timezone
 * @returns {string} Today's date string in YYYY-MM-DD format
 */
export const getTodayLocal = () => {
  return formatDateLocal(getCurrentDate());
};

/**
 * Parse a date string and return a Date object
 * Handles various input formats safely
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDate = (dateInput) => {
  if (!dateInput) return null;
  
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }
  
  const parsed = new Date(dateInput);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Safely parse a YYYY-MM-DD date string as a local date (avoiding UTC timezone shifts)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date|null} Date object representing local date or null if invalid
 */
export const parseYYYYMMDDAsLocal = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  
  // Match YYYY-MM-DD format
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  
  const [, year, month, day] = match;
  
  // Create date using local timezone (month is 0-indexed in Date constructor)
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  // Verify the date is valid and matches the input
  if (isNaN(date.getTime()) || 
      date.getFullYear() !== parseInt(year) ||
      date.getMonth() !== parseInt(month) - 1 ||
      date.getDate() !== parseInt(day)) {
    return null;
  }
  
  return date;
};

/**
 * Compare two dates in local timezone (ignoring time)
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2, null if invalid
 */
export const compareDatesLocal = (date1, date2) => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  
  if (!d1 || !d2) return null;
  
  const dateStr1 = formatDateLocal(d1);
  const dateStr2 = formatDateLocal(d2);
  
  if (dateStr1 < dateStr2) return -1;
  if (dateStr1 > dateStr2) return 1;
  return 0;
};

/**
 * Check if two dates are the same day in local timezone
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if same day, false otherwise
 */
export const isSameDay = (date1, date2) => {
  return compareDatesLocal(date1, date2) === 0;
};

/**
 * Get date range for common periods using configured timezone
 * @param {string} period - 'today', 'yesterday', 'last7Days', 'last30Days', 'last120Days', 'lastWeek', 'lastMonth', 'last3Months'
 * @returns {object} Object with startDate and endDate in YYYY-MM-DD format
 */
export const getDateRange = (period) => {
  const today = getCurrentDate();
  let startDate, endDate;
  
  switch (period) {
    case 'today':
      startDate = endDate = formatDateLocal(today);
      break;
      
    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      startDate = endDate = formatDateLocal(yesterday);
      break;
      
    case 'last7Days':
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      startDate = formatDateLocal(sevenDaysAgo);
      endDate = formatDateLocal(today);
      break;
      
    case 'last30Days':
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      startDate = formatDateLocal(thirtyDaysAgo);
      endDate = formatDateLocal(today);
      break;
      
    case 'last120Days':
      const oneHundredTwentyDaysAgo = new Date(today);
      oneHundredTwentyDaysAgo.setDate(today.getDate() - 120);
      startDate = formatDateLocal(oneHundredTwentyDaysAgo);
      endDate = formatDateLocal(today);
      break;
      
    case 'lastWeek':
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      startDate = formatDateLocal(weekAgo);
      endDate = formatDateLocal(today);
      break;
      
    case 'lastMonth':
      const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
      startDate = formatDateLocal(monthAgo);
      endDate = formatDateLocal(today);
      break;
      
    case 'last3Months':
      const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
      startDate = formatDateLocal(threeMonthsAgo);
      endDate = formatDateLocal(today);
      break;
      
    case 'currentMonth':
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = formatDateLocal(firstOfMonth);
      endDate = formatDateLocal(today);
      break;
      
    case 'currentWeek':
      const dayOfWeek = today.getDay();
      const firstOfWeek = new Date(today);
      firstOfWeek.setDate(today.getDate() - dayOfWeek);
      startDate = formatDateLocal(firstOfWeek);
      endDate = formatDateLocal(today);
      break;
      
    default:
      startDate = endDate = formatDateLocal(today);
  }
  
  return { startDate, endDate };
};

/**
 * Add days to a date in local timezone
 * @param {Date|string} date - Base date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date in YYYY-MM-DD format
 */
export const addDaysLocal = (date, days) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;
  
  const newDate = new Date(parsedDate);
  newDate.setDate(parsedDate.getDate() + days);
  return formatDateLocal(newDate);
};

/**
 * Get the start and end of a week for a given date
 * @param {Date|string} date - Date within the week
 * @param {number} weekStart - Day of week to start (0 = Sunday, 1 = Monday)
 * @returns {object} Object with startDate and endDate
 */
export const getWeekRange = (date, weekStart = 0) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;
  
  const dayOfWeek = parsedDate.getDay();
  const diffToStart = (dayOfWeek - weekStart + 7) % 7;
  
  const startDate = new Date(parsedDate);
  startDate.setDate(parsedDate.getDate() - diffToStart);
  
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  
  return {
    startDate: formatDateLocal(startDate),
    endDate: formatDateLocal(endDate)
  };
};

/**
 * Get the start and end of a month for a given date
 * @param {Date|string} date - Date within the month
 * @returns {object} Object with startDate and endDate
 */
export const getMonthRange = (date) => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return null;
  
  const startDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1);
  const endDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth() + 1, 0);
  
  return {
    startDate: formatDateLocal(startDate),
    endDate: formatDateLocal(endDate)
  };
};

/**
 * Check if a date is within a range (inclusive)
 * @param {Date|string} date - Date to check
 * @param {Date|string} startDate - Range start date
 * @param {Date|string} endDate - Range end date
 * @returns {boolean} True if date is within range
 */
export const isDateInRange = (date, startDate, endDate) => {
  const checkDate = parseDate(date);
  const rangeStart = parseDate(startDate);
  const rangeEnd = parseDate(endDate);
  
  if (!checkDate || !rangeStart || !rangeEnd) return false;
  
  const checkDateStr = formatDateLocal(checkDate);
  const startDateStr = formatDateLocal(rangeStart);
  const endDateStr = formatDateLocal(rangeEnd);
  
  return checkDateStr >= startDateStr && checkDateStr <= endDateStr;
};

/**
 * Format date for API calls (ensures consistent format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date for API
 */
export const formatForAPI = (date) => {
  return formatDateLocal(parseDate(date));
};

/**
 * Get array of dates between two dates (inclusive)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {string[]} Array of date strings in YYYY-MM-DD format
 */
export const getDatesBetween = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  
  if (!start || !end || start > end) return [];
  
  const dates = [];
  const current = new Date(start);
  
  while (current <= end) {
    dates.push(formatDateLocal(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
};

// ========================
// CONFIGURATION HELPERS
// ========================

/**
 * Get the current timezone configuration
 * @returns {string} Current timezone configuration
 */
export const getCurrentTimezoneConfig = () => {
  try {
    const storedTimezone = localStorage.getItem('arcattend_timezone_config');
    return storedTimezone || TIMEZONE_CONFIG;
  } catch (error) {
    console.warn('Failed to read timezone from localStorage:', error);
    return TIMEZONE_CONFIG;
  }
};

/**
 * Get the resolved timezone identifier being used
 * @returns {string|undefined} Timezone identifier or undefined for local
 */
export const getResolvedTimezone = () => {
  return getConfiguredTimezone();
};

/**
 * Get information about the current timezone configuration
 * @returns {object} Timezone configuration info
 */
export const getTimezoneInfo = () => {
  const currentConfig = getCurrentTimezoneConfig();
  const timezone = getConfiguredTimezone();
  const sampleDate = new Date();
  
  return {
    config: currentConfig,
    resolvedTimezone: timezone,
    isLocal: currentConfig === 'LOCAL',
    isUTC: timezone === 'UTC',
    sampleFormatted: formatDateLocal(sampleDate),
    sampleReadable: formatDateReadable(sampleDate)
  };
};

/**
 * Set a new timezone configuration and persist it
 * @param {string} newTimezone - The new timezone to use
 */
export const setTimezoneConfig = (newTimezone) => {
  // Validate the timezone
  const validTimezones = ['LOCAL', 'UTC', 'EST', 'PST', 'CST', 'MST'];
  if (!validTimezones.includes(newTimezone) && !newTimezone.includes('/')) {
    console.warn(`Invalid timezone: ${newTimezone}. Using LOCAL instead.`);
    newTimezone = 'LOCAL';
  }
  
  // Update the configuration
  TIMEZONE_CONFIG = newTimezone;
  
  // Persist to localStorage
  try {
    localStorage.setItem('arcattend_timezone_config', newTimezone);
  } catch (error) {
    console.warn('Failed to save timezone to localStorage:', error);
  }
};

/**
 * Get available timezone options for UI selection
 * Returns all 24 timezones matching backend's SUPPORTED_TIMEZONES
 * @returns {Array} Array of timezone option objects
 */
export const getAvailableTimezones = () => {
  return [
    // US Timezones (6)
    {
      value: 'LOCAL',
      label: 'Local Device Time',
      description: 'Use your device\'s current timezone'
    },
    {
      value: 'America/New_York',
      label: 'Eastern Time (New York)',
      description: 'US Eastern timezone (UTC-5/-4)'
    },
    {
      value: 'America/Chicago',
      label: 'Central Time (Chicago)',
      description: 'US Central timezone (UTC-6/-5)'
    },
    {
      value: 'America/Denver',
      label: 'Mountain Time (Denver)',
      description: 'US Mountain timezone (UTC-7/-6)'
    },
    {
      value: 'America/Los_Angeles',
      label: 'Pacific Time (Los Angeles)',
      description: 'US Pacific timezone (UTC-8/-7)'
    },
    {
      value: 'UTC',
      label: 'UTC (Coordinated Universal Time)',
      description: 'Universal time standard (UTC+0)'
    },
    
    // International Timezones (18 additional to complete 24 total)
    {
      value: 'Pacific/Midway',
      label: 'Samoa (Midway)',
      description: 'Samoa timezone (UTC-11)'
    },
    {
      value: 'Pacific/Honolulu',
      label: 'Hawaii (Honolulu)',
      description: 'Hawaii timezone (UTC-10)'
    },
    {
      value: 'America/Anchorage',
      label: 'Alaska (Anchorage)',
      description: 'Alaska timezone (UTC-9/-8)'
    },
    {
      value: 'America/Mexico_City',
      label: 'Mexico City',
      description: 'Central Mexico timezone (UTC-6/-5)'
    },
    {
      value: 'America/Bogota',
      label: 'Bogota',
      description: 'Colombia timezone (UTC-5)'
    },
    {
      value: 'America/Caracas',
      label: 'Caracas',
      description: 'Venezuela timezone (UTC-4)'
    },
    {
      value: 'America/Santiago',
      label: 'Santiago',
      description: 'Chile timezone (UTC-4/-3)'
    },
    {
      value: 'America/St_Johns',
      label: 'Newfoundland (St. Johns)',
      description: 'Newfoundland timezone (UTC-3:30/-2:30)'
    },
    {
      value: 'Atlantic/Cape_Verde',
      label: 'Cape Verde',
      description: 'Cape Verde timezone (UTC-1)'
    },
    {
      value: 'Europe/London',
      label: 'London',
      description: 'UK timezone (UTC+0/+1)'
    },
    {
      value: 'Europe/Paris',
      label: 'Paris',
      description: 'Central European timezone (UTC+1/+2)'
    },
    {
      value: 'Europe/Athens',
      label: 'Athens',
      description: 'Eastern European timezone (UTC+2/+3)'
    },
    {
      value: 'Europe/Moscow',
      label: 'Moscow',
      description: 'Moscow timezone (UTC+3)'
    },
    {
      value: 'Asia/Dubai',
      label: 'Dubai',
      description: 'Gulf timezone (UTC+4)'
    },
    {
      value: 'Asia/Karachi',
      label: 'Karachi',
      description: 'Pakistan timezone (UTC+5)'
    },
    {
      value: 'Asia/Dhaka',
      label: 'Dhaka',
      description: 'Bangladesh timezone (UTC+6)'
    },
    {
      value: 'Asia/Bangkok',
      label: 'Bangkok',
      description: 'Indochina timezone (UTC+7)'
    },
    {
      value: 'Asia/Shanghai',
      label: 'Shanghai',
      description: 'China timezone (UTC+8)'
    },
    {
      value: 'Asia/Tokyo',
      label: 'Tokyo',
      description: 'Japan timezone (UTC+9)'
    },
    {
      value: 'Australia/Sydney',
      label: 'Sydney',
      description: 'Australian Eastern timezone (UTC+10/+11)'
    },
    {
      value: 'Pacific/Noumea',
      label: 'New Caledonia (Noumea)',
      description: 'New Caledonia timezone (UTC+11)'
    },
    {
      value: 'Pacific/Auckland',
      label: 'Auckland',
      description: 'New Zealand timezone (UTC+12/+13)'
    }
  ];
};